import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {useIssueState} from '@github-ui/issue-pr-state'
import {issuePath} from '@github-ui/paths'
import {TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, ButtonGroup, Tooltip} from '@primer/react'

import type {PotentialDuplicateIssue} from './PotentialDuplicates'
import styles from './PotentialDuplicatesMenu.module.css'

export type PotentialDuplicatesMenuProps = {
  items: readonly PotentialDuplicateIssue[]
  repoOwner: string
  repoName: string
  onIssueClick?: (issue: PotentialDuplicateIssue) => void
  trackIssueClicked: (position: number) => void
}

function IssueIcon({
  state,
  stateReason,
}: {
  state: PotentialDuplicateIssue['state']
  stateReason: PotentialDuplicateIssue['stateReason']
}) {
  const {sourceIcon} = useIssueState({state, stateReason})
  const Icon = sourceIcon('Issue')
  // eslint-disable-next-line react-hooks/static-components
  return <Icon />
}

export function PotentialDuplicatesMenu({
  items,
  repoOwner,
  repoName,
  onIssueClick,
  trackIssueClicked,
}: PotentialDuplicatesMenuProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  const firstIssue = items[0]
  if (!firstIssue) {
    return null
  }

  const issueUrl = issuePath({owner: repoOwner, repo: repoName, issueNumber: firstIssue.number})
  const hovercardAttrs = getHovercardAttributesForType('issue', {
    owner: repoOwner,
    repo: repoName,
    issueNumber: firstIssue.number,
  })

  const handleFirstIssueClick = (e: React.MouseEvent) => {
    trackIssueClicked(1)

    if (!onIssueClick) return

    // Allow cmd/ctrl+click and middle-click to open in new tab
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.button === 1 || e.shiftKey || e.ctrlKey || e.metaKey) return

    e.preventDefault()
    onIssueClick(firstIssue)
  }

  const handleDropdownIssueClick = (e: React.MouseEvent, issue: PotentialDuplicateIssue, index: number) => {
    trackIssueClicked(index + 1)

    if (!onIssueClick) return

    // Allow cmd/ctrl+click and middle-click to open in new tab
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.button === 1 || e.shiftKey || e.ctrlKey || e.metaKey) return

    e.preventDefault()
    onIssueClick(issue)
  }

  const firstIssueButton = (
    <Button
      as="a"
      href={issueUrl}
      {...hovercardAttrs}
      target={onIssueClick ? undefined : '_blank'}
      onClick={handleFirstIssueClick}
      size="small"
      className={styles.firstButton}
      leadingVisual={() => <IssueIcon state={firstIssue.state} stateReason={firstIssue.stateReason} />}
    >
      <span className={styles.issueTitle}>
        {firstIssue.title}&nbsp;<span className={styles.mutedText}>#{firstIssue.number}</span>
      </span>
    </Button>
  )

  // When there's only one issue, render without ButtonGroup to preserve rounded corners
  if (items.length === 1) {
    return firstIssueButton
  }

  return (
    <ButtonGroup data-issue-and-pr-hovercards-enabled>
      {firstIssueButton}
      <ActionMenu>
        <Tooltip type="label" text="View all potential duplicates">
          <ActionMenu.Button className={styles.actionMenuButton} icon={TriangleDownIcon} size="small" />
        </Tooltip>
        <ActionMenu.Overlay width="medium" align="end" side="outside-top">
          <ActionList>
            {items.map((issue, index) => {
              return (
                <ActionList.LinkItem
                  key={issue.id}
                  href={issuePath({owner: repoOwner, repo: repoName, issueNumber: issue.number})}
                  target={onIssueClick ? undefined : '_blank'}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleDropdownIssueClick(e, issue, index)}
                >
                  <ActionList.LeadingVisual>
                    <IssueIcon state={issue.state} stateReason={issue.stateReason} />
                  </ActionList.LeadingVisual>
                  <span>{issue.title}</span>
                  <span className={styles.mutedText}>&nbsp;#{issue.number}</span>
                </ActionList.LinkItem>
              )
            })}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </ButtonGroup>
  )
}
